import { Link, Navigate } from '@tanstack/react-router';
import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { loginErrorMessage } from '../../public/auth-errors.js';
import { PasswordInput } from '../../public/PasswordInput.js';
import { alertLinkClasses, PUBLIC_FORM, quietLinkClasses } from '../../public/classes.js';
import { PublicShell } from '../../public/PublicShell.js';
import { api, ApiError } from '../../shared/api.js';
import { useMe } from '../../shared/auth.js';
import { Button, buttonClasses } from '../../ui/Button.js';
import { Field, Input } from '../../ui/Field.js';
import { FormActions } from '../../ui/FormActions.js';
import { Alert } from '../../ui/States.js';
import { useDemoEnabled } from '../lib/demo.js';
import { HELP_HOME } from '../../help/paths.js';
import { landingPath } from '../lib/paths.js';
import { useEnterSession } from '../lib/session.js';

/**
 * Connexion : compte nominatif, identique sur téléphone et ordinateur. Ensuite (vague 5) :
 * console `/admin`, écran de choix, ou adresse `/g/<slug>/` du garage (`landingPath`).
 */
export default function LoginPage() {
  const me = useMe();
  const enter = useEnterSession();
  // Vague 7 : « Essayer la démonstration » seulement si le serveur l'annonce (`DEMO_ENABLED`).
  const demoEnabled = useDemoEnabled() === true;
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const login = useMutation({
    mutationFn: () => api('auth.login', { body: { email, password } }),
    onSuccess: (data) => enter(data, { afterLogin: true }),
  });

  if (me.data && !login.isPending && !login.isSuccess) return <Navigate to={landingPath(me.data)} replace />;

  const error = loginErrorMessage(login.error);
  const locked = login.error instanceof ApiError && login.error.status === 429;
  const badCredentials = login.error instanceof ApiError && (login.error.status === 401 || login.error.status === 400);

  return (
    <PublicShell
      title="Connexion"
      intro="Accédez à l’espace de votre garage."
      note={<>Vous êtes client d’un garage ? Votre suivi s’ouvre depuis le lien reçu par e-mail, sans compte.</>}
    >
      <form
        className={PUBLIC_FORM}
        onSubmit={(e) => {
          e.preventDefault();
          login.mutate();
        }}
      >
        <Field label="E-mail">
          <Input
            type="email"
            name="email"
            autoComplete="username"
            inputMode="email"
            autoCapitalize="none"
            spellCheck={false}
            required
            value={email}
            aria-invalid={badCredentials || undefined}
            onChange={(e) => setEmail(e.target.value)}
          />
        </Field>
        <div>
          <Field label="Mot de passe">
            <PasswordInput
              name="password"
              autoComplete="current-password"
              required
              value={password}
              aria-invalid={badCredentials || undefined}
              onChange={(e) => setPassword(e.target.value)}
            />
          </Field>
          <div className="mt-1 flex justify-end">
            <Link to="/forgot-password" className={quietLinkClasses}>
              Mot de passe oublié ?
            </Link>
          </div>
        </div>
        {error ? (
          <Alert
            tone={locked ? 'warning' : 'danger'}
            role="alert"
            action={
              locked ? (
                <Link to="/forgot-password" className={alertLinkClasses}>
                  Choisir un nouveau mot de passe
                </Link>
              ) : undefined
            }
          >
            {error}
          </Alert>
        ) : null}
        <FormActions>
          <Button type="submit" size="lg" block loading={login.isPending}>
            Se connecter
          </Button>
        </FormActions>
      </form>
      {demoEnabled ? (
        <div className="mt-6 border-t border-line pt-5">
          <p className="mb-2.5 text-sm text-ink-3">Pas encore de compte ? Découvrez l’espace atelier sur un garage fictif.</p>
          <Link to="/demo" className={buttonClasses({ variant: 'secondary', block: true })}>
            Essayer la démonstration
          </Link>
        </div>
      ) : null}
      <p className="mt-5 text-center">
        <Link to={HELP_HOME.team} className={quietLinkClasses}>
          Aide et mode d’emploi
        </Link>
      </p>
    </PublicShell>
  );
}
